<template>
  <app-page class="pay-page" fill lean>
    <!-- Kopf wie bei Verkäufen (Kundenwunsch 13.10.2026): leichter Titel, rechts die Aktionen mit Icon und Beschriftung
         (auf dem Handy Icon-Knöpfe); „Alle Gruppen auf-/zuklappen“ immer sichtbar, ohne Gruppierung gesperrt -->
    <template #actions>
      <template v-if="!handy">
        <app-button
          :icon="expanded ? 'fold' : 'unfold'"
          :disabled="!groupSelection"
          @click="collapseAllClick"
        >{{ expanded ? 'Alle zuklappen' : 'Alle aufklappen' }}</app-button>
        <app-button icon="refresh" @click="refreshDataGrid">Aktualisieren</app-button>
        <app-button icon="download" @click="exportToExcel">Excel-Export</app-button>
      </template>
      <template v-else>
        <app-icon-button
          :icon="expanded ? 'fold' : 'unfold'"
          :label="expanded ? 'Alle Gruppen zuklappen' : 'Alle Gruppen aufklappen'"
          :disabled="!groupSelection"
          @click="collapseAllClick"
        />
        <app-icon-button icon="refresh" label="Aktualisieren" @click="refreshDataGrid" />
        <app-icon-button icon="download" label="Excel-Export" @click="exportToExcel" />
      </template>
      <app-button v-if="hasPermission('REST_PAYMENT_POST')" icon="plus" @click="addPayment">Zahlung erfassen</app-button>
    </template>
    <!-- Werkzeugleiste wie bei Verkäufen: links Monat und Suche, rechts Filter (Panel wie bei Verkäufen statt der
         Filterzeile unter dem Tabellenkopf) und Gruppierung -->
    <template #toolbar>
      <app-month-stepper v-model="month" />
      <app-search v-model="searchText" placeholder="In Zahlungen suchen" @input="onSearchInput" />
    </template>
    <template #toolbar-end>
      <sale-filter-menu
        :value="filters"
        :facets="facets"
        :result-count="resultCount"
        :description="filterTooltip"
        :segment-defs="noSegments"
        :active-filters="activeFilterCount"
        range-label="Betrag"
        :result-nouns="resultNouns"
        :empty-value="emptyPaymentFilters"
        :align-right="!handy"
        @open="refreshFacets"
        @input="onFiltersInput"
      />
      <app-select
        v-model="groupSelection"
        :items="groupingOptions"
        icon="layers"
        hide-prefix
        prefix="Gruppierung"
        @input="onGroupingChange"
      />
    </template>

    <!-- Druckblatt „Quittung“: nur während des Drucks, unsichtbar außerhalb des Fensters gerendert -->
    <div v-if="receiptSheet" class="pay-print-source" aria-hidden="true">
      <payment-receipt-sheet ref="receiptSheet" v-bind="receiptSheet" />
    </div>
    <!-- Briefkopf wählen, wenn der Mandant mehrere Betriebe hat (receiptLetterheads.js) -->
    <receipt-letterhead-dialog
      :value="!!letterheadPick"
      :choices="letterheadPick ? letterheadPick.choices : []"
      @input="letterheadPick = null"
      @choose="onLetterheadChoose"
    />
    <!-- Meldung liegt fest über der Seite und nimmt keinen Platz im Inhalt ein -->
    <!-- Toast unten rechts wie bei PayPal (Kundenwunsch 13.10.2026) -->
    <app-toast v-model="snackbar" icon="printer" text="Quittung wird vorbereitet …" indeterminate closable />

    <!-- Tabelle füllt die Höhe über der Fußleiste (wie Verkäufe und Benutzer; Kundenwunsch 13.10.2026: die Fußleiste
         steht auch bei wenig Inhalt unten); auf dem Handy wächst sie mit ihren Zeilen -->
    <!-- Speichern-Leiste über der Tabelle (Kundenwunsch 13.10.2026: „lieber über der tabelle“) -->
    <app-save-bar v-if="hasChanges" top @save="saveChanges" @discard="revertChanges" />
    <div class="app-grid-fill pay-grid" :class="{ 'pay-grid--auto': handy }">
      <dx-data-grid
        id="paymentsDataGrid"
        :ref="gridRefName"
        :height="gridHeight"
        :data-source="devicesDataSource"
        :show-borders="false"
        :show-column-lines="false"
        :show-row-lines="true"
        :column-auto-width="true"
        :allow-column-reordering="!gridTouch"
        :allow-column-resizing="!gridTouch"
        :selection="selection"
        :hover-state-enabled="!gridTouch"
        :element-attr="gridAttr"
        @cell-prepared="cellPrepared"
        @content-ready="onContentReady"
        @toolbar-preparing="onToolbarPreparing($event)"
        @editing-start="onEditingStart"
        @row-inserting="onDataChanged"
        @row-updating="onDataChanged"
        @row-removing="onDataChanged"
        :scrolling="scrolling"
      >
      <dx-state-storing
          :enabled="true"
          type="custom"
          storage-key="paymentStorage"
          :customLoad="customLoad"
          :customSave="customSave"
        />
        <DxColumn
          data-field="pay_date"
          caption="Zahlungsdatum"
          data-type="date"
          sortOrder="desc"
        />
        <DxColumn
          data-field="billing_month"
          caption="Abrechnungsmonat"
          data-type="number"
          :visible="$store.state.customerId == 1 || $store.state.customerId == 10"
        />
        <DxColumn
          data-field="billing_year"
          caption="Abrechnungsjahr"
          data-type="number"
          :visible="$store.state.customerId == 1 || $store.state.customerId == 10"
        />
        <DxColumn
          data-field="id"
          caption="Zahlungs-ID"
          :allow-editing="false"
        />
        <DxColumn data-field="user_id" caption="Benutzer">
          <DxLookup
            :data-source="driverDataSource"
            value-expr="user_id"
            display-expr="fullname"
          />
        </DxColumn>
        <!-- eigener Name: zwei Spalten mit demselben dataField brechen in DevExtreme mit E1059 ab -->
        <DxColumn data-field="user_id" name="benutzer_id" caption="Benutzer-ID">
          <DxLookup
            :data-source="driver"
            value-expr="user_id"
            display-expr="user_id"
          />
        </DxColumn>
        <DxColumn
          data-field="amount"
          caption="Betrag"
          data-type="number"
          format="######0.00 €"
        />
        <DxColumn data-field="comment" caption="Bemerkung" />
        <DxColumn data-field="pay_place" caption="Bezahlt wo" />
        <DxColumn type="buttons" :buttons="buttons" />
        <dx-group-panel :visible="false" />
        <dx-grouping :auto-expand-all="expanded" expandMode="rowClick" />
        <dx-paging :page-size="defaultPageSize" />
        <!-- Seitenwahl und Blättern in der Fußleiste (AppGridFooter) -->
        <dx-pager :visible="false" />
        <dx-export
          :enabled="false"
          :allow-export-selected-data="true"
          file-name="Zahlungen"
          :texts="{
            exportAll: 'Alle Zahlungen herunterladen',
            exportSelectedRows: 'Ausgewählte Zahlungen herunterladen',
            exportTo: 'Excel Download',
          }"
        />
        <dx-editing
          refresh-mode="full"
          :allow-adding="hasPermission('REST_PAYMENT_POST')"
          :allow-updating="hasPermission('REST_PAYMENT_PUT')"
          :allow-deleting="hasPermission('REST_PAYMENT_DELETE')"
          mode="batch"
        />
      </dx-data-grid>
    </div>
    <app-grid-footer ref="fuss" :page-sizes="pageSizes">
      <template #summary>
        <span>Summe <strong>{{ summaryTotal }}</strong></span>
        <span class="app-sep" aria-hidden="true">·</span>
        <span v-if="narrowed"><strong>{{ summaryCount }}</strong> von {{ summaryLoaded }}</span>
        <span v-else><strong>{{ summaryCount }}</strong> {{ summaryNoun }}</span>
      </template>
    </app-grid-footer>
  </app-page>
</template>

<script>
import {
  DxDataGrid,
  DxPaging,
  DxPager,
  DxExport,
  DxGrouping,
  DxGroupPanel,
  DxColumn,
  DxLookup,
  DxEditing,
  DxStateStoring,
} from "devextreme-vue/data-grid";
import deMessages from "devextreme/localization/messages/de.json";
import { locale, loadMessages } from "devextreme/localization";
import CustomStore from "devextreme/data/custom_store";
// import ArrayStore from "devextreme/data/array_store";
// import DataSource from "devextreme/data/data_source";
import axios from "axios";
import moment from "moment-timezone";
import { hasPermission } from "@/auth/auth";
import DataGridMixin from "@/mixins/DataGridMixin";
import query from "devextreme/data/query";
import { formatEuro, formatNumber, plural } from "@/utils/format";
import SaleFilterMenu from "@/components/sales/SaleFilterMenu";
import PaymentReceiptSheet from "@/components/payments/PaymentReceiptSheet";
import ReceiptLetterheadDialog from "@/components/payments/ReceiptLetterheadDialog";
import { extraLetterheads } from "@/components/payments/receiptLetterheads";
import { loadLetterheadLogo, loadCustomer, letterheadOf, printSheetsHtml, simaPrintLogoUrl } from "@/utils/printSheet";
import {
  emptyFilters,
  activeCount,
  buildPredicate,
  buildFacets,
  describeFilters,
  NO_VALUE_TEXT,
} from "@/components/sales/saleFilters";
import { PAYMENT_FILTER_CONFIG } from "@/components/payments/paymentFilters";

const RESULT_NOUNS = ["Zahlung", "Zahlungen"];
const NO_SEGMENTS = [];

export default {
  components: {
    DxDataGrid,
    DxPaging,
    DxPager,
    DxExport,
    DxGrouping,
    DxGroupPanel,
    DxColumn,
    DxLookup,
    DxEditing,
    DxStateStoring,
    SaleFilterMenu,
    PaymentReceiptSheet,
    ReceiptLetterheadDialog,
  },
  mixins: [DataGridMixin],
  computed: {
    handy() {
      return !!(this.$dichte && this.$dichte.handy);
    },
    // Handy: AppPage fill wirkt dort nicht, das Grid wächst mit seinen Zeilen; sonst füllt es die Höhe
    gridHeight() {
      return this.handy ? undefined : "100%";
    },
    // Fußleiste: Summe der Beträge und Anzahl nach Filter und Suche, „4 von 24 Zahlungen“ solange eingeschränkt
    narrowed() {
      return this.summary.count !== this.summary.loaded;
    },
    summaryTotal() {
      return formatEuro(this.summary.total);
    },
    summaryCount() {
      return formatNumber(this.summary.count);
    },
    summaryLoaded() {
      return formatNumber(this.summary.loaded) + " " + plural(this.summary.loaded, "Zahlung", "Zahlungen");
    },
    summaryNoun() {
      return plural(this.summary.count, "Zahlung", "Zahlungen");
    },
    activeFilterCount() {
      return activeCount(this.filters, PAYMENT_FILTER_CONFIG);
    },
    filterTooltip() {
      const parts = describeFilters(this.filters, this.filterLabel, 3, PAYMENT_FILTER_CONFIG);
      return parts.length ? parts.join(" · ") : null;
    },
    dataUrl() {
      return `${
        this.$store.state.baseUrl
      }payment?pay_month=${this.month.substring(
        5,
        7
      )}&pay_year=${this.month.substring(0, 4)}`;
    },
  },
  created() {
    loadMessages(deMessages);
    locale("de"); // immer Deutsch wie src/plugins/devextreme.js (vorher Browsersprache)
  },
  data() {
    return {
      storageKey: "paymentStorage",
      customer: this.$store.state.customerId,
      user: this.$store.state.cognitoUsername,
      month: new Date().toISOString().substr(0, 7),
      driver: [],
      driverDataSource: {
        store: [],
      },
      gridRefName: "paymentsDataGrid",
      // Filter-Panel (Kundenwunsch 13.10.2026): Zustand, Werte der Listen, Treffer nach Filter und Suche
      filters: emptyFilters(PAYMENT_FILTER_CONFIG),
      facets: [],
      resultCount: null,
      summary: { total: 0, count: 0, loaded: 0 },
      // Daten des Druckblatts „Quittung“ (nur während des Drucks)
      receiptSheet: null,
      // offene Auswahl des Briefkopfs { payment, choices } (nur bei Mandanten mit mehreren Betrieben)
      letterheadPick: null,
      noSegments: NO_SEGMENTS,
      resultNouns: RESULT_NOUNS,
      search: "",
      expanded: false,
      searchText: "",
      groupSelection: null,
      groupingOptions: [
        // Icon statt Präfix wie bei Verkäufen (Kundenwunsch 13.10.2026): Wert allein, ohne Gruppierung nur „Gruppierung“
        // (wie „Filter“); „Gruppierung: …“ als Name und Tooltip
        { value: null, text: 'Keine Gruppierung', buttonText: 'Gruppierung', ariaText: 'keine' },
        { value: 'user', text: 'Fahrer' }
      ],
      // Auswahl einzeln (L9: verschachteltes multiple entfernt); Objekt-Optionen nicht als Literal im Template
      selection: { mode: 'single' },
      gridAttr: {},
      hasChanges: false,
      snackbar: false,
      devicesDataSource: new CustomStore({
        key: ["id", "customer_id"],
        // geladene Zahlungen merken: Werte der Filterlisten (nicht reaktiv)
        load: () => axios.get(this.dataUrl).then((response) => {
          this._rows = Array.isArray(response.data) ? response.data : [];
          this.refreshFacets();
          return response;
        }),
        insert: async (newPayment) => {
          newPayment.pay_date =
            moment(newPayment.pay_date)
              .tz("Europe/Berlin")
              .format("YYYY-MM-DD") + "T00:00:00.000Z";
          await axios.post(
            `${this.$store.state.baseUrl}payment?customer_id=${this.$store.state.customerId}`,
            newPayment
          );
          this.refreshDataGrid();
        },
        update: async (paymentKeys, paymentUpdates) => {
          paymentUpdates.pay_date =
            moment(paymentUpdates.pay_date)
              .tz("Europe/Berlin")
              .format("YYYY-MM-DD") + "T00:00:00.000Z";
          await axios.put(
            `${this.$store.state.baseUrl}payment?customer_id=${this.$store.state.customerId}&id=${paymentKeys.id}`,
            paymentUpdates
          );
          this.refreshDataGrid();
        },
        remove: async (paymentKeys) => {
          await axios.delete(
            `${this.$store.state.baseUrl}payment?customer_id=${this.$store.state.customerId}&id=${paymentKeys.id}`
          );
          this.refreshDataGrid();
        },
      }),
      buttons: [
        {
          hint: "Quittung drucken",
          icon: "print",
          visible: true,
          onClick: this.printReceipt,
        },
      ],
    };
  },
  watch: {
    dataUrl: function () {
      this.refreshDataGrid();
    },
  },
  methods: {
    customLoad: function () {
        var state = localStorage.getItem(this.storageKey);
        if (state) {
            state = JSON.parse(state);
            // Siehe Saldo.vue: doppelte Spaltennamen aus einem alten Zustand
            // lassen DevExtreme mit E1059 abbrechen, das Grid bleibt dann leer.
            var columns = state.columns || [];
            var names = columns.map(function (column) {
                return column.name || column.dataField;
            });
            if (new Set(names).size !== names.length) {
                localStorage.removeItem(this.storageKey);
                return null;
            }
            for (var i = 0; i < columns.length; i++) {
                columns[i].filterValue = null;
            }
        }
        return state;
    },
    customSave: function (state) {  
        localStorage.setItem(this.storageKey, JSON.stringify(state));  
    },  
    hasPermission(permissionName) {
      return hasPermission(permissionName);
    },
    // Quittung drucken (Kundenwunsch 13.10.2026): Inhalt wie die frühere Word-Vorlage, Kopf, Fuß und Erzeugung im
    // Browser wie die Fahrerabrechnung (statt POST document?template=PaymentReceiptTemplate.docx und print-js).
    // Hat der Mandant weitere Betriebe (receiptLetterheads.js, Kundenwunsch 20.10.2026), zuerst den Briefkopf wählen.
    async printReceipt(event) {
      const payment = event.row.data;
      const extras = extraLetterheads(this.$store.state.customerId);
      if (!extras.length) {
        await this.printReceiptWith(payment, null);
        return;
      }
      const customer = await loadCustomer(this.$store);
      this.letterheadPick = {
        payment,
        choices: [{ key: "mandant", letterhead: letterheadOf(customer, this.$store.state.customerName) }].concat(extras),
      };
    },
    onLetterheadChoose(choice) {
      const pick = this.letterheadPick;
      this.letterheadPick = null;
      if (pick) {
        this.printReceiptWith(pick.payment, choice.key === "mandant" ? null : choice);
      }
    },
    // choice: weiterer Briefkopf aus receiptLetterheads.js, null = Briefkopf des Mandanten
    async printReceiptWith(payment, choice) {
      const user = this.driver.find((d) => d.user_id === payment.user_id);
      this.snackbar = true;
      try {
        const [logoUrl, letterhead] = await Promise.all([
          loadLetterheadLogo(this.$store.state.baseUrl, choice && choice.logo),
          choice
            ? choice.letterhead
            : loadCustomer(this.$store).then((customer) => letterheadOf(customer, this.$store.state.customerName)),
        ]);
        this.receiptSheet = {
          payment: Object.assign({}, payment),
          driverName: user ? user.fullname || user.username : "",
          letterhead,
          logoUrl,
          simaLogoUrl: simaPrintLogoUrl(),
          createdAt: moment().format("DD.MM.YYYY HH:mm"),
        };
        await this.$nextTick();
        const sheet = this.$refs.receiptSheet;
        if (sheet && sheet.$el) {
          await printSheetsHtml(sheet.$el.outerHTML, "Quittung " + payment.id);
        }
      } finally {
        this.snackbar = false;
        this.receiptSheet = null;
      }
    },
    async loadDriver() {
      this.driver = (
        await axios.get(
          `${this.$store.state.baseUrl}user?customer_id=${this.$store.state.customerId}`
        )
      ).data;
      this.driverDataSource = {
        store: this.driver,
        sort: "fullname",
      };
      // console.log(this.driverDataSource.store());
    },
    cellPrepared(e) {
      if (e.rowType === "data") {
        if (e.data.datum < new Date(2019, 11, 12)) {
          e.cellElement.style.color = "#AAAAAA";
        }
        if (e.data.betrag > 5) {
          if (e.column.dataField === "sale_id") {
            e.cellElement.style.fontWeight = "bold";
          }
          if (e.column.dataField === "price") {
            e.cellElement.style.backgroundColor = "#FFBB00";
            e.cellElement.style.color = "#000000";
          }
        }
      }

      if (e.rowType === "groupFooter" && e.column.dataField === "betrag") {
        e.cellElement.style.fontStyle = "italic";
      }
    },

    refreshDataGrid() {
      this.$refs[this.gridRefName].instance.refresh();
    },
    // ---------------------------------------------------------------- Filter-Panel (Kundenwunsch 13.10.2026)
    emptyPaymentFilters() {
      return emptyFilters(PAYMENT_FILTER_CONFIG);
    },
    filterLabel(listKey, key) {
      if (key === "") {
        return NO_VALUE_TEXT;
      }
      if (listKey === "users") {
        const user = this.driver.find((d) => String(d.user_id) === key);
        return (user && (user.fullname || user.username)) || key;
      }
      return key;
    },
    refreshFacets() {
      this.facets = Object.freeze(buildFacets(this._rows || [], this.filters, this.filterLabel, PAYMENT_FILTER_CONFIG));
    },
    // Filter als Prüffunktion auf der Datenquelle wie bei Verkäufen (jedes Mal eine neue Funktion)
    onFiltersInput(filters) {
      this.filters = filters || this.emptyPaymentFilters();
      const grid = this.$refs[this.gridRefName] && this.$refs[this.gridRefName].instance;
      if (grid) {
        const match = buildPredicate(this.filters, PAYMENT_FILTER_CONFIG);
        grid.filter(match ? [match, "=", true] : null);
      }
      this.refreshFacets();
    },
    onContentReady(e) {
      const grid = e.component;
      const count = Number(grid.totalCount());
      this.resultCount = count >= 0 ? count : 0;
      const rows = this._rows || [];
      const filter = grid.getCombinedFilter();
      const visible = filter && rows.length ? query(rows).filter(filter).toArray() : rows;
      this.summary = {
        total: visible.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
        count: this.resultCount,
        loaded: rows.length,
      };
      if (this.$refs.fuss) {
        this.$refs.fuss.update(grid);
      }
    },
    onSearchInput() {
      if (this.$refs[this.gridRefName] && this.$refs[this.gridRefName].instance) {
        this.$refs[this.gridRefName].instance.searchByText(this.searchText || '');
      }
    },
    collapseAllClick() {
      this.expanded = !this.expanded;
    },
    exportToExcel() {
      this.$refs[this.gridRefName].instance.exportToExcel(false);
    },
    onGroupingChange() {
      this.groupChanged({ value: this.groupSelection });
    },
    addPayment() {
      this.$refs[this.gridRefName].instance.addRow();
      this.hasChanges = true;
    },
    saveChanges() {
      this.$refs[this.gridRefName].instance.saveEditData();
      this.hasChanges = false;
    },
    revertChanges() {
      this.$refs[this.gridRefName].instance.cancelEditData();
      this.hasChanges = false;
    },
    onEditingStart() {
      this.hasChanges = true;
    },
    onDataChanged() {
      this.hasChanges = true;
    },
    onBtnOverview() {
      this.expanded = false;
      this.$refs[this.gridRefName].instance.clearGrouping();
    },
    onBtnDriver() {
      this.expanded = false;
      this.$refs[this.gridRefName].instance.clearGrouping();
      this.$refs[this.gridRefName].instance.columnOption(
        "user_id",
        "groupIndex",
        0
      );
    },
    onToolbarPreparing(e) {
      // Remove default editing buttons (add, save, revert) from toolbar
      e.toolbarOptions.items = e.toolbarOptions.items.filter(
        item => item.name !== 'addRowButton' &&
                item.name !== 'saveButton' &&
                item.name !== 'revertButton'
      );
      // ohne eigene Einträge die Grid-Werkzeugleiste ganz ausblenden (die Aktionen stehen in der Titelzeile)
      if (!e.toolbarOptions.items.length) {
        e.toolbarOptions.visible = false;
      }
    },
    groupChanged(e) {
      switch (e.value) {
        case "user":
          this.onBtnDriver();
          break;
        default:
          this.onBtnOverview();
      }
    },
  },
  mounted() {
    this.loadDriver();
  },
};
</script>

<style scoped>
/* Druckblatt „Quittung“: nur als HTML-Quelle für das Druck-iframe, außerhalb des Fensters */
.pay-print-source {
  position: fixed;
  top: 0;
  left: -10000px;
  width: 760px;
  pointer-events: none;
}
/* Handy: Grid in natürlicher Höhe */
.pay-grid.pay-grid--auto {
  flex: none;
}
</style>
